import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Premium dark sonner toaster.
 *
 * Top-right placement so toasts don't collide with the page-level CTA bar
 * at the bottom of dash-v2 pages. Mint accent, slim border, soft shadow,
 * tabular numerics where applicable. Success toasts feel like a stamp
 * landing; errors feel firm but not loud.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      position="top-right"
      offset={20}
      gap={10}
      visibleToasts={4}
      closeButton
      style={{ zIndex: 9999, fontFamily: "'Inter Tight', system-ui, sans-serif" }}
      theme={theme as ToasterProps["theme"]}
      duration={3500}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-[#0e1111]/95 group-[.toaster]:backdrop-blur-md group-[.toaster]:text-[#f5f5f5] group-[.toaster]:border group-[.toaster]:border-[#1c2020] group-[.toaster]:shadow-[0_8px_32px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.02)_inset] group-[.toaster]:rounded-[12px] group-[.toaster]:px-4 group-[.toaster]:py-3 group-[.toaster]:text-[13px]",
          title: "group-[.toast]:text-[13px] group-[.toast]:font-semibold",
          description: "group-[.toast]:text-[#9ca3a3] group-[.toast]:text-[12px] group-[.toast]:mt-0.5",
          actionButton:
            "group-[.toast]:bg-[#00e676] group-[.toast]:text-[#070808] group-[.toast]:font-semibold group-[.toast]:rounded-md group-[.toast]:text-[12px]",
          cancelButton:
            "group-[.toast]:bg-transparent group-[.toast]:text-[#9ca3a3] group-[.toast]:hover:text-[#f5f5f5]",
          closeButton:
            "group-[.toast]:bg-[#0a0c0c] group-[.toast]:border-[#1c2020] group-[.toast]:text-[#9ca3a3] group-[.toast]:hover:text-[#f5f5f5]",
          success:
            "group-[.toaster]:!border-l-2 group-[.toaster]:!border-l-[#00e676]",
          error:
            "group-[.toaster]:!border-l-2 group-[.toaster]:!border-l-[#ff5757]",
          warning:
            "group-[.toaster]:!border-l-2 group-[.toaster]:!border-l-[#ffb800]",
          info:
            "group-[.toaster]:!border-l-2 group-[.toaster]:!border-l-[#00bfff]",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
